import React, { useEffect, useRef, useState } from "react";
import "./ProductFilters.scss";
import {
  Typography,
  Select,
  Slider,
  Checkbox,
  Switch,
  Button,
  Input,
  Drawer,
  Badge,
  Divider,
} from "antd";
import { CloseOutlined, FunnelPlotOutlined } from "@ant-design/icons";

const GENETICS = [
  { label: "Indica", value: "indica" },
  { label: "Sativa", value: "sativa" },
  { label: "Hybrid", value: "hybrid" },
];

const SORTS = [
  { label: "Default", value: undefined },
  { label: "Price: Low to High", value: "ascending" },
  { label: "Price: High to Low", value: "descending" },
];

// Desktop lists this many categories before "Show all categories".
const VISIBLE_CATEGORIES = 8;

// Desktop panel: the sticky search row above it (64px) plus the 16px gap,
// and the gap kept under it when it pins at the bottom.
const STICKY_TOP = 80;
const STICKY_BOTTOM_GAP = 16;
const STICK_TOP = { position: "sticky", top: STICKY_TOP };

const toggleIn = (list, value) =>
  list?.includes(value)
    ? list?.filter((item) => item !== value)
    : [...(list || []), value];

export default function ProductFilters({
  token,
  isMobile,
  isFilterOpened,
  onClose,
  loading,
  categories,
  catgeoriesLoading,
  selectedCategories,
  onCategoryChange,
  filterOption,
  brands,
  brandsLoading,
  selectedBrands,
  onBrandChange,
  selectedGenetics,
  onGeneticsChange,
  priceRange,
  onPriceRangeChange,
  isOnsale,
  onCheckboxChange,
  maxPrice,
  sortValue,
  onOrderChange,
  onOrderClear,
  activeCount,
  resultsCount,
  onClearAll,
  onReset,
}) {
  const [showAllCategories, setShowAllCategories] = useState(false);
  const formatter = (value) => `$${value}`;

  const selectedCategoryIds =
    categories
      ?.filter((category) => selectedCategories?.includes(category?.name))
      ?.map((category) => category?.id) || [];

  // Same call the multi-select made — (ids, [{ label, value }]) — so the hook
  // keeps writing category names to the URL and reloading the brand list.
  const changeCategories = (ids) => {
    if (typeof onCategoryChange !== "function") {
      return;
    }
    onCategoryChange(
      ids,
      categories
        ?.filter((category) => ids?.includes(category?.id))
        ?.map((category) => ({ label: category?.name, value: category?.id }))
    );
  };

  const changeSort = (value) => {
    if (value && typeof onOrderChange === "function") {
      onOrderChange(value);
    } else if (!value && typeof onOrderClear === "function") {
      onOrderClear();
    }
  };

  // The price boxes hold what the customer is typing as a draft, apart from
  // the applied range. Applying every keystroke broke typing twice over: the
  // "2" of "250" is below a minimum of 100, and the products request a
  // half-typed number started disabled the box and dropped the focus.
  //   - while typing, a pause applies the draft only when it already makes
  //     sense against the other end, and never rewrites what is being typed;
  //   - leaving the box, or Enter, applies it fitted into 0..maxPrice and
  //     kept on its side of the other end;
  //   - the slider and the URL write into a box only while it is not focused.
  const [priceDraft, setPriceDraft] = useState(["", ""]);
  const focusedPrice = useRef(null);
  const priceIdle = useRef(null);
  const priceFrom = priceRange?.[0];
  const priceTo = priceRange?.[1];

  useEffect(() => {
    setPriceDraft((draft) => [
      focusedPrice.current === 0 ? draft[0] : `${priceFrom ?? ""}`,
      focusedPrice.current === 1 ? draft[1] : `${priceTo ?? ""}`,
    ]);
  }, [priceFrom, priceTo]);

  useEffect(() => () => clearTimeout(priceIdle.current), []);

  // Desktop panel. When it fits under the search row it just sticks there.
  // When it is taller than that, it moves with the page in the direction of
  // the scroll and pins as soon as its top (scrolling up) or bottom (scrolling
  // down) reaches the edge, so every filter can be reached and the panel never
  // leaves the screen. Between the two pinned states it is placed relative to
  // its flow position, so switching never makes it jump.
  const panelRef = useRef(null);
  const [stick, setStick] = useState(STICK_TOP);

  useEffect(() => {
    const panel = panelRef.current;
    if (isMobile || !panel) return undefined;
    let mode = "top";
    let lastParentTop = null;

    const place = () => {
      const parent = panel.parentElement;
      const rect = panel.getBoundingClientRect();
      const parentTop = parent.getBoundingClientRect().top;
      const fits =
        rect.height + STICKY_TOP + STICKY_BOTTOM_GAP <= window.innerHeight;
      const down = lastParentTop !== null && parentTop < lastParentTop;
      const up = lastParentTop !== null && parentTop > lastParentTop;
      lastParentTop = parentTop;

      const free = () => {
        mode = "free";
        setStick({ position: "relative", top: rect.top - parentTop });
      };
      if (fits) {
        mode = "top";
        setStick(STICK_TOP);
      } else if (down && mode !== "bottom") {
        if (rect.bottom <= window.innerHeight - STICKY_BOTTOM_GAP) {
          mode = "bottom";
          setStick({
            position: "sticky",
            top: window.innerHeight - rect.height - STICKY_BOTTOM_GAP,
          });
        } else if (mode === "top") free();
      } else if (up && mode !== "top") {
        if (rect.top >= STICKY_TOP) {
          mode = "top";
          setStick(STICK_TOP);
        } else if (mode === "bottom") free();
      }

      // While it slides, whatever moved up past the line the panel pins at
      // stays hidden, so it never shows half-cut under the search row. Not
      // when the end of the list pushes it away: then it goes under the row
      // at the same edge as the products. Set on the element so a scroll
      // while it moves with the page does not re-render.
      const hidden = mode === "top" ? 0 : Math.max(0, STICKY_TOP - rect.top);
      panel.style.clipPath = hidden ? `inset(${hidden}px 0 0 0)` : "";
    };

    place();
    const observer = new ResizeObserver(place);
    observer.observe(panel);
    // Capture also catches a scrolling ancestor, not only the window.
    document.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      observer.disconnect();
      document.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [isMobile]);

  const applyPrice = (index, text, { fit }) => {
    const fallback = index === 0 ? 0 : maxPrice;
    let value = text === "" ? fallback : Math.min(Number(text), maxPrice);
    const other = priceRange[index === 0 ? 1 : 0];
    const crosses = index === 0 ? value > other : value < other;
    if (crosses) {
      if (!fit) {
        return;
      }
      value = other;
    }
    if (fit) {
      setPriceDraft((draft) =>
        draft.map((item, i) => (i === index ? `${value}` : item))
      );
    }
    if (value !== priceRange[index]) {
      onPriceRangeChange(
        index === 0 ? [value, priceRange[1]] : [priceRange[0], value]
      );
    }
  };

  const onPriceType = (index, raw) => {
    const text = `${raw}`.replace(/\D/g, "");
    setPriceDraft((draft) =>
      draft.map((item, i) => (i === index ? text : item))
    );
    clearTimeout(priceIdle.current);
    if (text !== "") {
      priceIdle.current = setTimeout(
        () => applyPrice(index, text, { fit: false }),
        400
      );
    }
  };

  const renderPriceInput = (index, label) => (
    <Input
      aria-label={label}
      className="productFilters-cont__form__formItem__priceInp"
      prefix="$"
      inputMode="numeric"
      value={priceDraft[index]}
      onFocus={() => {
        focusedPrice.current = index;
      }}
      onChange={(e) => onPriceType(index, e.target.value)}
      onBlur={() => {
        focusedPrice.current = null;
        clearTimeout(priceIdle.current);
        applyPrice(index, priceDraft[index], { fit: true });
      }}
      onPressEnter={() => {
        clearTimeout(priceIdle.current);
        applyPrice(index, priceDraft[index], { fit: true });
      }}
    />
  );

  const visibleCategories =
    isMobile || showAllCategories
      ? categories
      : categories?.filter(
          (category, index) =>
            index < VISIBLE_CATEGORIES ||
            selectedCategoryIds?.includes(category?.id)
        );

  const renderLabel = (text) => (
    <Typography.Text
      className="productFilters-cont__form__formItem__text"
      style={{
        color: token?.Typography?.mainColorText,
      }}>
      {text}
    </Typography.Text>
  );

  const renderPill = (label, active, onClick, disabled) => (
    <Button
      key={label}
      shape="round"
      size={isMobile ? "large" : "middle"}
      type={active ? "primary" : "default"}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}>
      {label}
    </Button>
  );

  const divider = isMobile ? null : (
    <Divider
      className="productFilters-cont__form__divider"
      style={{
        borderColor: token?.Layout?.footerBorderTopColor,
      }}
    />
  );

  const form = (
    <div className="productFilters-cont__form">
      {isMobile ? (
        <div className="productFilters-cont__form__formItem">
          {renderLabel("Sort by")}
          <div className="productFilters-cont__form__formItem__pills">
            {SORTS?.map((sort) =>
              renderPill(
                sort?.label,
                sortValue === sort?.value,
                () => changeSort(sort?.value),
                loading
              )
            )}
          </div>
        </div>
      ) : null}
      <div className="productFilters-cont__form__formItem">
        {renderLabel("Categories")}
        {isMobile ? (
          <div className="productFilters-cont__form__formItem__pills">
            {visibleCategories?.map((category) =>
              renderPill(
                category?.name || "",
                selectedCategoryIds?.includes(category?.id),
                () =>
                  changeCategories(toggleIn(selectedCategoryIds, category?.id)),
                loading || catgeoriesLoading
              )
            )}
          </div>
        ) : (
          <>
            <Checkbox.Group
              className="productFilters-cont__form__formItem__checkList"
              disabled={loading || catgeoriesLoading}
              value={selectedCategoryIds}
              onChange={changeCategories}>
              {visibleCategories?.map((category) => (
                <Checkbox key={category?.id} value={category?.id}>
                  <Typography.Text
                    style={{
                      color: token?.Typography?.mainColorText,
                    }}>
                    {category?.name || ""}
                  </Typography.Text>
                </Checkbox>
              ))}
            </Checkbox.Group>
            {categories?.length > VISIBLE_CATEGORIES ? (
              <Button
                className="productFilters-cont__form__formItem__link"
                type="link"
                size="small"
                onClick={() => setShowAllCategories(!showAllCategories)}>
                {showAllCategories ? "Show less" : "Show all categories"}
              </Button>
            ) : null}
          </>
        )}
      </div>
      {divider}
      <div className="productFilters-cont__form__formItem">
        {renderLabel("Brand")}
        <Select
          placeholder="Search brands"
          allowClear
          showSearch
          size="large"
          disabled={loading || brandsLoading}
          loading={brandsLoading}
          value={selectedBrands}
          options={brands?.map((brand) => ({
            label: brand?.label ? brand?.label : "",
            value: brand?.id ? `${brand?.id}` : "",
          }))}
          onChange={onBrandChange}
          filterOption={filterOption}
          mode="multiple"
        />
      </div>
      {divider}
      <div className="productFilters-cont__form__formItem">
        {renderLabel("Genetics")}
        <div className="productFilters-cont__form__formItem__pills">
          {GENETICS?.map((genetic) =>
            renderPill(
              genetic?.label,
              selectedGenetics?.includes(genetic?.value),
              () =>
                onGeneticsChange(toggleIn(selectedGenetics, genetic?.value)),
              loading
            )
          )}
        </div>
      </div>
      {maxPrice && priceRange?.length ? (
        <>
          {divider}
          <div className="productFilters-cont__form__formItem">
            <div className="productFilters-cont__form__formItem__row">
              {renderLabel("Price Range")}
              {isMobile ? (
                <Typography.Text
                  className="productFilters-cont__form__formItem__text"
                  style={{
                    color: token?.Typography?.colorPrimaryText,
                  }}>
                  {`${formatter(priceRange[0])} – ${formatter(priceRange[1])}`}
                </Typography.Text>
              ) : null}
            </div>
            {isMobile ? null : (
              <div className="productFilters-cont__form__formItem__row">
                {renderPriceInput(0, "Minimum price")}
                <Typography.Text>to</Typography.Text>
                {renderPriceInput(1, "Maximum price")}
              </div>
            )}
            <div className="productFilters-cont__form__formItem__wrapper">
              <Slider
                style={{
                  width: "calc(100% - 14px)",
                }}
                range
                disabled={loading}
                tooltip={{
                  formatter,
                }}
                min={0}
                max={maxPrice}
                onChange={onPriceRangeChange}
                value={priceRange}
              />
            </div>
          </div>
        </>
      ) : null}
      {divider}
      <div className="productFilters-cont__form__saleRow">
        {renderLabel("On sale only")}
        <Switch
          aria-label="On sale only"
          checked={isOnsale}
          // The hook reads e.target.checked, as it did from the checkbox.
          onChange={(checked) => onCheckboxChange({ target: { checked } })}
        />
      </div>
    </div>
  );

  const title = (
    <div className="productFilters-cont__title">
      {isMobile ? null : (
        <FunnelPlotOutlined
          style={{
            color: token?.icons?.colorPrimaryIcon,
            fontSize: "16px",
          }}
        />
      )}
      <Typography.Text
        className="productFilters-cont__title__text"
        style={{
          color: token?.Typography?.mainColorText,
        }}>
        {isMobile ? "Filter & sort" : "Filters"}
      </Typography.Text>
      <Badge count={activeCount} color={token?.colorPrimary} />
      {!isMobile && activeCount ? (
        <Button
          className="productFilters-cont__title__clear"
          type="link"
          size="small"
          disabled={loading}
          onClick={onClearAll}>
          Clear all
        </Button>
      ) : null}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        className="productFilters-drawer"
        style={{
          backgroundColor: token?.Drawer?.colorBgDrawer,
        }}
        placement="bottom"
        size="85%"
        open={Boolean(isFilterOpened)}
        onClose={onClose}
        title={title}
        closeIcon={
          <CloseOutlined
            style={{
              color: token?.icons?.whiteIcon,
              fontSize: "20px",
            }}
          />
        }
        footer={
          <div className="productFilters-drawer__footer">
            <Button size="large" disabled={loading} onClick={onReset}>
              Reset
            </Button>
            <Button size="large" type="primary" onClick={onClose}>
              {`Show ${resultsCount ? resultsCount : 0} results`}
            </Button>
          </div>
        }>
        <div className="productFilters-cont">{form}</div>
      </Drawer>
    );
  }

  return (
    <div
      ref={panelRef}
      className="productFilters-cont"
      style={{
        backgroundColor: token?.Card?.colorBgCard,
        ...stick,
      }}>
      {title}
      {form}
    </div>
  );
}
